import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Download, EyeOff, Eye, Loader2, Trash2 } from "lucide-react";

// Athlete-facing: exactly who sees what, in plain words, with one tap to stop sharing.
export default function MyData() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/me/data-access").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);

  const setSharing = async (share) => {
    if (!share && !window.confirm("Stop sharing new data with your squad? You can turn it back on any time.")) return;
    setBusy(true);
    try {
      await api.post("/me/data-access/sharing", { share });
      toast.success(share ? "Sharing is back on" : "Sharing stopped · nothing new will be added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change that"); }
    finally { setBusy(false); }
  };

  const exportMine = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/me/export");
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = `my-trifit-data.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded your copy");
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setBusy(false); }
  };

  const eraseRequest = async () => {
    if (!window.confirm("Ask your academy to erase your data? Sharing turns off straight away.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/me/erase-request");
      toast.success("Request logged", { description: data.message });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the request"); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  if (!d.linked) {
    return (
      <div className="p-6 sm:p-10 max-w-2xl" data-testid="my-data-unlinked">
        <ShieldCheck className="w-6 h-6" />
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight mt-3">Your data</h1>
        <p className="text-sm text-muted-foreground mt-3">
          No athlete profile is linked to this login yet. Accept the invitation sent to your email by your academy admin,
          and this page will show exactly who can see what.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="my-data-page">
      <header className="min-h-16 border-b border-border flex flex-wrap items-center gap-3 px-4 sm:px-8 py-3">
        <ShieldCheck className="w-5 h-5" />
        <h1 className="font-heading text-lg sm:text-xl font-black tracking-tight">Your data</h1>
        <span className="text-xs text-muted-foreground truncate">{d.name}{d.squad ? ` · ${d.squad}` : ""}</span>
        <span data-testid="my-data-state"
          className={`ml-auto text-[10px] uppercase tracking-widest px-2 py-1 border ${d.sharing ? "border-traffic-green text-traffic-green" : "border-traffic-amber text-traffic-amber"}`}>
          {d.sharing ? "Sharing on" : "Sharing off"}
        </span>
      </header>

      <div className="p-4 sm:p-8 max-w-3xl space-y-6">
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs" data-testid="my-data-counts">
          <span><b className="font-heading text-base tabular-nums">{d.counts.sessions}</b> <span className="text-muted-foreground">sessions held</span></span>
          <span><b className="font-heading text-base tabular-nums">{d.counts.days_of_wearable_data}</b> <span className="text-muted-foreground">days of watch data</span></span>
          <span><b className="font-heading text-base tabular-nums">{d.counts.injuries}</b> <span className="text-muted-foreground">open injuries</span></span>
        </div>

        <div className="border border-border" data-testid="my-data-rows">
          {d.rows.map((r, i) => (
            <div key={i} className="p-4 border-b border-border last:border-0" data-testid={`my-data-row-${i}`}>
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">{r.who}</p>
              <p className="text-sm mt-1.5 leading-relaxed">{r.sees}</p>
              {r.why && <p className="text-[11px] text-muted-foreground mt-1">{r.why}</p>}
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed">{d.note}</p>

        <div className="flex flex-wrap gap-3">
          {d.sharing ? (
            <button data-testid="my-data-withdraw" onClick={() => setSharing(false)} disabled={busy}
              className="text-sm border border-foreground px-4 py-2.5 hover:bg-surface transition-colors flex items-center gap-2 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <EyeOff className="w-4 h-4" />} Stop sharing
            </button>
          ) : (
            <button data-testid="my-data-restore" onClick={() => setSharing(true)} disabled={busy}
              className="text-sm bg-foreground text-background px-4 py-2.5 hover:bg-neutral-800 transition-colors flex items-center gap-2 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Share again
            </button>
          )}
          <button data-testid="my-data-export" onClick={exportMine} disabled={busy}
            className="text-sm border border-border px-4 py-2.5 hover:bg-surface transition-colors flex items-center gap-2 disabled:opacity-40">
            <Download className="w-4 h-4" /> Download my copy
          </button>
          <button data-testid="my-data-erase" onClick={eraseRequest} disabled={busy}
            className="text-sm border border-border px-4 py-2.5 text-muted-foreground hover:text-traffic-red hover:border-traffic-red transition-colors flex items-center gap-2 disabled:opacity-40">
            <Trash2 className="w-4 h-4" /> Ask for erasure
          </button>
        </div>
        {d.withdrawn_at && (
          <p className="text-[11px] text-traffic-amber" data-testid="my-data-withdrawn-at">
            Sharing stopped on {String(d.withdrawn_at).slice(0, 10)}.
          </p>
        )}
      </div>
    </div>
  );
}
